import React, { useEffect, useState } from "react";
import {
  Search,
  Loader2,
  Download,
  Save,
  Mail,
  Crown,
  Trash2,
  Globe,
  Phone,
  Instagram,
  Facebook,
  Linkedin,
  CheckSquare,
  Square,
  X,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtErr = (e, f) => e?.response?.data?.detail || e?.message || f || "Something went wrong.";

const AREAS = [
  "Paisley & Renfrewshire",
  "Glasgow",
  "West of Scotland",
];

const STATUS_STYLE = {
  new: "text-cyan-300 border-cyan-400/40 bg-cyan-400/10",
  contacted: "text-amber-300 border-amber-400/40 bg-amber-400/10",
  client: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
};

const socialIcon = (u) => {
  const s = (u || "").toLowerCase();
  if (s.includes("instagram")) return <Instagram size={13} />;
  if (s.includes("facebook")) return <Facebook size={13} />;
  if (s.includes("linkedin")) return <Linkedin size={13} />;
  return null;
};

const ProspectsTab = () => {
  const [niche, setNiche] = useState("");
  const [area, setArea] = useState(AREAS[0]);
  const [count, setCount] = useState(20);
  const [finding, setFinding] = useState(false);
  const [results, setResults] = useState(null); // {prospects, source_status, ...}
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);

  const [saved, setSaved] = useState([]);
  const [counts, setCounts] = useState({ new: 0, contacted: 0, client: 0 });
  const [filter, setFilter] = useState("all");
  const [loadingSaved, setLoadingSaved] = useState(true);

  const [emailModal, setEmailModal] = useState(null); // prospect being emailed
  const [busyId, setBusyId] = useState(null);

  const loadSaved = async () => {
    try {
      const { data } = await api.get("/prospects");
      setSaved(data.items || []);
      setCounts(data.counts || { new: 0, contacted: 0, client: 0 });
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't load your prospects."));
    } finally {
      setLoadingSaved(false);
    }
  };

  useEffect(() => {
    loadSaved();
  }, []);

  const find = async () => {
    if (!niche.trim()) return toast.error("Type a business type (e.g. 'gyms').");
    setFinding(true);
    setResults(null);
    setSelected(new Set());
    try {
      const { data } = await api.post("/prospects/find", { niche, area, count: Number(count) || 20 });
      setResults(data);
      setSelected(new Set(data.prospects.map((_, i) => i)));
      if (!data.count) toast.error("No prospects found — try a broader term or area.");
      else toast.success(`Found ${data.count} prospects (${data.scraped_count} scraped, ${data.ai_count} AI).`);
    } catch (e) {
      toast.error(fmtErr(e, "Search failed."));
    } finally {
      setFinding(false);
    }
  };

  const toggleSel = (i) => {
    const s = new Set(selected);
    s.has(i) ? s.delete(i) : s.add(i);
    setSelected(s);
  };

  const importSelected = async () => {
    if (!results) return;
    const chosen = results.prospects.filter((_, i) => selected.has(i));
    if (!chosen.length) return toast.error("Select at least one prospect.");
    setImporting(true);
    try {
      const { data } = await api.post("/prospects/import", { prospects: chosen });
      toast.success(`Imported ${data.inserted}${data.skipped ? `, skipped ${data.skipped} duplicates` : ""}.`);
      setResults(null);
      loadSaved();
    } catch (e) {
      toast.error(fmtErr(e, "Import failed."));
    } finally {
      setImporting(false);
    }
  };

  const exportCsv = () => {
    if (!results?.prospects?.length) return;
    const cols = ["business_name", "category", "area", "website", "phone", "email", "instagram", "facebook", "linkedin"];
    const esc = (v) => `"${String(v || "").replace(/"/g, '""')}"`;
    const rows = [cols.join(",")].concat(
      results.prospects.filter((_, i) => selected.has(i)).map((p) => cols.map((c) => esc(p[c])).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `prospects-${niche.replace(/\s+/g, "-")}-${area.replace(/\s+/g, "-")}.csv`;
    a.click();
  };

  const setStatus = async (p, status) => {
    setBusyId(p.prospect_id);
    try {
      await api.patch(`/prospects/${p.prospect_id}`, { status });
      loadSaved();
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't update."));
    } finally {
      setBusyId(null);
    }
  };

  const promote = async (p) => {
    setBusyId(p.prospect_id);
    try {
      await api.post(`/prospects/${p.prospect_id}/promote`, {});
      toast.success(`${p.business_name} is now a Mogul Client.`);
      loadSaved();
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't promote."));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (p) => {
    setBusyId(p.prospect_id);
    try {
      await api.delete(`/prospects/${p.prospect_id}`);
      loadSaved();
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't delete."));
    } finally {
      setBusyId(null);
    }
  };

  const shown = saved.filter((p) => filter === "all" || p.status === filter);

  return (
    <div data-testid="admin-prospects">
      <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
        <Search size={20} className="text-cyan-300" /> Prospect Finder
      </h2>
      <p className="text-slate-400 text-sm mt-1">
        Generate a list of prospective local-business clients, import the ones you want, work them
        through your pipeline, and promote winners to Mogul Clients.
      </p>

      {/* Finder */}
      <div className="mt-6 p-5 bg-[#0A192F] border border-white/10 rounded-sm">
        <div className="grid sm:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Business type / niche</label>
            <input
              value={niche}
              onChange={(e) => setNiche(e.target.value)}
              placeholder="e.g. cafes, gyms, hairdressers"
              data-testid="prospects-niche-input"
              className="mt-1 w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Area</label>
            <input
              list="prospect-areas"
              value={area}
              onChange={(e) => setArea(e.target.value)}
              data-testid="prospects-area-input"
              className="mt-1 w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
            />
            <datalist id="prospect-areas">
              {AREAS.map((a) => <option key={a} value={a} />)}
            </datalist>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Count</label>
            <input
              type="number"
              min="1"
              max="40"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              data-testid="prospects-count-input"
              className="mt-1 w-20 px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
            />
          </div>
          <button
            onClick={find}
            disabled={finding}
            data-testid="prospects-find-btn"
            className="px-5 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-xs uppercase tracking-widest hover:bg-[#33F3FF] disabled:opacity-50 inline-flex items-center gap-2"
          >
            {finding ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Find
          </button>
        </div>
        {finding && (
          <p className="text-xs text-slate-500 mt-3">Scraping directories and filling gaps with AI… this can take ~30s.</p>
        )}
      </div>

      {/* Results */}
      {results && (
        <div className="mt-4 p-5 bg-[#0A192F] border border-cyan-400/20 rounded-sm" data-testid="prospects-results">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <p className="text-sm text-white font-semibold">
              {results.count} prospects · {selected.size} selected
            </p>
            <div className="flex gap-2">
              <button onClick={exportCsv} data-testid="prospects-export-btn" className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 text-slate-200 hover:border-white/30 rounded-sm text-xs uppercase tracking-wider">
                <Download size={13} /> CSV
              </button>
              <button onClick={importSelected} disabled={importing} data-testid="prospects-import-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-400/15 border border-cyan-400/40 text-cyan-200 hover:border-cyan-400 rounded-sm text-xs uppercase tracking-wider disabled:opacity-50">
                {importing ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Import selected
              </button>
            </div>
          </div>
          <div className="max-h-[420px] overflow-auto space-y-2">
            {results.prospects.map((p, i) => (
              <button
                key={i}
                onClick={() => toggleSel(i)}
                data-testid={`prospect-result-${i}`}
                className={`w-full text-left flex items-start gap-3 p-3 rounded-sm border transition-colors ${
                  selected.has(i) ? "border-cyan-400/40 bg-cyan-400/5" : "border-white/10 hover:border-white/20"
                }`}
              >
                {selected.has(i) ? <CheckSquare size={16} className="text-cyan-300 mt-0.5 shrink-0" /> : <Square size={16} className="text-slate-500 mt-0.5 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-semibold truncate">{p.business_name}</span>
                    <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${p.source === "ai" ? "text-fuchsia-300 bg-fuchsia-400/10" : "text-slate-400 bg-white/5"}`}>{p.source}</span>
                  </div>
                  <p className="text-xs text-slate-400">{[p.category, p.area].filter(Boolean).join(" · ")}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px] text-slate-400">
                    {p.website && <span className="inline-flex items-center gap-1"><Globe size={11} />{p.website.replace(/^https?:\/\//, "").slice(0, 30)}</span>}
                    {p.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{p.phone}</span>}
                    {p.email && <span className="inline-flex items-center gap-1"><Mail size={11} />{p.email}</span>}
                    {[p.instagram, p.facebook, p.linkedin].filter(Boolean).map((s, k) => (
                      <span key={k} className="inline-flex items-center gap-1">{socialIcon(s)}</span>
                    ))}
                  </div>
                </div>
              </button>
            ))}
          </div>
          <details className="mt-3">
            <summary className="text-xs text-slate-500 cursor-pointer">Source diagnostics</summary>
            <div className="mt-2 space-y-1">
              {results.source_status.map((s, i) => (
                <p key={i} className={`text-[11px] font-mono ${s.ok ? "text-emerald-400" : "text-slate-500"}`}>
                  {s.ok ? "✓" : "✗"} {s.source} {s.error ? `— ${s.error}` : ""}
                </p>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* Saved pipeline */}
      <div className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-heading text-lg font-bold text-white">Your pipeline</h3>
          <div className="flex gap-1.5">
            {[["all", saved.length], ["new", counts.new], ["contacted", counts.contacted], ["client", counts.client]].map(([k, n]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`prospects-filter-${k}`}
                className={`px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider border ${filter === k ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-slate-400 hover:border-white/20"}`}
              >
                {k} {n != null ? `(${n})` : ""}
              </button>
            ))}
          </div>
        </div>

        {loadingSaved ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-cyan-300" /></div>
        ) : shown.length === 0 ? (
          <p className="text-slate-500 text-sm mt-4" data-testid="prospects-empty">No prospects here yet — run a search above and import some.</p>
        ) : (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="prospects-saved-list">
            {shown.map((p) => (
              <div key={p.prospect_id} data-testid={`prospect-card-${p.prospect_id}`} className="p-4 bg-[#0A192F] border border-white/10 rounded-sm flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-white truncate">{p.business_name}</p>
                    <p className="text-xs text-slate-400">{[p.category, p.area].filter(Boolean).join(" · ")}</p>
                  </div>
                  <span className={`shrink-0 text-[9px] font-mono uppercase px-2 py-0.5 rounded border ${STATUS_STYLE[p.status] || ""}`}>{p.status}</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-400">
                  {p.website && <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-cyan-300"><Globe size={11} />site</a>}
                  {p.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{p.phone}</span>}
                  {p.email && <span className="inline-flex items-center gap-1 truncate max-w-[140px]"><Mail size={11} />{p.email}</span>}
                </div>

                <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap gap-1.5">
                  <select
                    value={p.status}
                    onChange={(e) => setStatus(p, e.target.value)}
                    disabled={busyId === p.prospect_id}
                    data-testid={`prospect-status-${p.prospect_id}`}
                    className="px-2 py-1 bg-[#050B14] border border-white/10 text-slate-200 text-xs rounded-sm outline-none"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="client">Client</option>
                  </select>
                  <button
                    onClick={() => setEmailModal(p)}
                    data-testid={`prospect-email-${p.prospect_id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-cyan-400/30 text-cyan-300 hover:border-cyan-400 rounded-sm text-xs"
                  >
                    <Mail size={12} /> Email
                  </button>
                  {p.status !== "client" && (
                    <button
                      onClick={() => promote(p)}
                      disabled={busyId === p.prospect_id}
                      data-testid={`prospect-promote-${p.prospect_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 border border-emerald-400/30 text-emerald-300 hover:border-emerald-400 rounded-sm text-xs disabled:opacity-50"
                    >
                      <Crown size={12} /> Mogul
                    </button>
                  )}
                  <button
                    onClick={() => remove(p)}
                    disabled={busyId === p.prospect_id}
                    data-testid={`prospect-delete-${p.prospect_id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-400/30 rounded-sm text-xs"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {emailModal && (
        <EmailModal prospect={emailModal} onClose={() => setEmailModal(null)} onSent={loadSaved} />
      )}
    </div>
  );
};

const EmailModal = ({ prospect, onClose, onSent }) => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [drafting, setDrafting] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/prospects/${prospect.prospect_id}/email`, { draft_only: true });
        setSubject(data.subject || "");
        setMessage(data.message || "");
      } catch (e) {
        toast.error(fmtErr(e, "Couldn't draft the email."));
      } finally {
        setDrafting(false);
      }
    })();
  }, [prospect.prospect_id]);

  const send = async () => {
    if (!prospect.email) return toast.error("This prospect has no email address. Add one first.");
    setSending(true);
    try {
      const { data } = await api.post(`/prospects/${prospect.prospect_id}/email`, { subject, message });
      if (data.status === "sent") {
        toast.success(`Sent to ${data.to}.`);
        onSent?.();
        onClose();
      } else {
        toast.error("Could not send.");
      }
    } catch (e) {
      toast.error(fmtErr(e, "Send failed."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="prospect-email-modal">
      <div className="w-full max-w-lg bg-[#0A192F] border border-cyan-400/30 rounded-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <h3 className="font-heading font-bold text-white text-sm flex items-center gap-2"><Mail size={15} className="text-cyan-300" /> Email {prospect.business_name}</h3>
          <button onClick={onClose} data-testid="prospect-email-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {!prospect.email && (
            <p className="text-xs text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-sm p-2">No email on file — you can review the draft, but sending needs an email address.</p>
          )}
          {drafting ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center"><Loader2 size={16} className="animate-spin" /> Drafting with AI…</div>
          ) : (
            <>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Subject</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="prospect-email-subject" className="mt-1 w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Message (to {prospect.email || "—"})</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={9} data-testid="prospect-email-body" className="mt-1 w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm resize-none" />
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white text-xs uppercase tracking-wider">Cancel</button>
          <button onClick={send} disabled={sending || drafting || !prospect.email} data-testid="prospect-email-send" className="inline-flex items-center gap-2 px-5 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-xs uppercase tracking-widest hover:bg-[#33F3FF] disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProspectsTab;
